import type { Freshness, SourceIssue } from './api'
import { dayLabel, subtractYen } from './format'
import type { PayrollSource } from './payrollSource'

// The wording and the "is this source behind?" predicate live here, outside the
// components, so the top-bar pill, the Overview stale line, the Data sources
// tab and the per-chart notes can never disagree about what "behind" means.

export const JOB_LABELS: Record<string, string> = {
  'deal-ledger': 'Deal ledger',
  'forecast-snapshot': 'Forecast',
  'cash-forecast-weekly': 'Weekly forecast copy',
  payroll: 'Payroll',
  freee: 'Costs (freee)',
  'gospel-ap': 'GOSPEL AP',
}

export const jobLabel = (job: string): string => JOB_LABELS[job] ?? job

// Behind = out of date OR its sheet failed its own checks. A broken sheet needs
// attention even while its last good data is inside the freshness budget.
export const isBehind = (source: Freshness): boolean => source.stale || source.issues.length > 0

export type SourceStatus = { text: string; tone: 'ok' | 'warn' }

// One wording for the Data sources table and the source map, so they can never
// disagree. Priority order: a sheet to fix outranks stale, which outranks a
// failed run.
export function sourceStatus(source: Freshness): SourceStatus {
  if (source.issues.length > 0) return { text: 'Sheet needs fixing', tone: 'warn' }
  if (source.stale) return { text: 'Out of date', tone: 'warn' }
  // A failing job is worth saying out loud even when its last success is still
  // inside the freshness budget: the data is current now and will silently rot
  // if nobody looks. It is not "behind" yet, so it keeps the ok tone.
  if (source.lastRunStatus === 'failed') return { text: 'Last run failed', tone: 'ok' }
  return { text: 'Current', tone: 'ok' }
}

export type FreshnessSummary = { tone: 'fresh' | 'warn'; text: string; behindCount: number }

export function freshnessSummary(freshness: Freshness[]): FreshnessSummary {
  const stale = freshness.filter((source) => source.stale).length
  const broken = freshness.filter((source) => source.issues.length > 0).length
  const behindCount = freshness.filter(isBehind).length
  // The pill's number must match the Data sources tab badge (behindCount). Once
  // a broken sheet sits beside a stale source, neither specific wording counts
  // both, so fall back to a generic one.
  const text =
    behindCount > stale && stale > 0
      ? `${behindCount} ${behindCount === 1 ? 'source needs' : 'sources need'} attention`
      : stale > 0
        ? `${stale} ${stale === 1 ? 'source' : 'sources'} out of date`
        : broken > 0
          ? `${broken} ${broken === 1 ? 'sheet needs' : 'sheets need'} fixing`
          : 'All sources current'
  return { tone: behindCount > 0 ? 'warn' : 'fresh', text, behindCount }
}

// Only staleness interrupts the Overview: the figures there are old. A sheet
// with issues but data still inside budget is the pill's job, not a banner's.
export function staleLine(freshness: Freshness[]): string | null {
  const labels = freshness.filter((source) => source.stale).map((source) => jobLabel(source.job))
  const last = labels[labels.length - 1]
  if (last === undefined) return null
  if (labels.length === 1) return `${last} isn't current.`
  return `${labels.slice(0, -1).join(', ')} and ${last} aren't current.`
}

// Which sources feed which chart, so a chart can say its own data is old.
export const CHART_SOURCES = {
  cashFlow: ['deal-ledger', 'forecast-snapshot', 'payroll'],
  cashPosition: ['deal-ledger', 'forecast-snapshot', 'payroll', 'freee'],
  breakeven: ['deal-ledger', 'payroll'],
  // Not only the bank feed: the computed balance it checks is built from the
  // same AR, payroll and plan-baseline inputs as cash flow.
  reconciliation: ['deal-ledger', 'forecast-snapshot', 'payroll', 'freee'],
  // The Monday copies, set against the live balance and the plan behind it.
  // The copy job is itself a tracked source. The card always reads the GOSPEL
  // series, so gospel-ap is listed outright and the swap below leaves it as is.
  forecastVsActual: [
    'cash-forecast-weekly',
    'forecast-snapshot',
    'deal-ledger',
    'freee',
    'gospel-ap',
  ],
} as const satisfies Record<string, readonly string[]>

// The same map for the chosen payroll source: with GOSPEL selected, the charts
// that follow the switch depend on the gospel-ap sync instead of payroll.
// Breakeven reads the software-team slice of the payroll tab either way.
export function chartSources(source: PayrollSource): Record<keyof typeof CHART_SOURCES, readonly string[]> {
  if (source === 'payroll-tab') return CHART_SOURCES
  const swap = (jobs: readonly string[]): readonly string[] =>
    jobs.map((job) => (job === 'payroll' ? 'gospel-ap' : job))
  return {
    cashFlow: swap(CHART_SOURCES.cashFlow),
    cashPosition: swap(CHART_SOURCES.cashPosition),
    breakeven: CHART_SOURCES.breakeven,
    reconciliation: swap(CHART_SOURCES.reconciliation),
    forecastVsActual: CHART_SOURCES.forecastVsActual,
  }
}

export function sourceNote(freshness: Freshness[], jobs: readonly string[]): string | null {
  const notes: string[] = []
  for (const job of jobs) {
    const source = freshness.find((candidate) => candidate.job === job)
    if (source === undefined || !isBehind(source)) continue
    notes.push(
      source.lastSucceededAt === null
        ? `${jobLabel(job)}: never loaded`
        : `${jobLabel(job)}: last good data ${dayLabel(source.lastSucceededAt.slice(0, 10))}`,
    )
  }
  return notes.length > 0 ? notes.join('; ') : null
}

// Sheet says minus rows add up to, in exact decimal arithmetic.
export const issueGap = (issue: SourceIssue): string =>
  subtractYen(issue.statedValue, issue.computedValue)
